/**
 * 67. Quality Inspections — inspection plans; results; disposition;
 * nonconformance.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatDate } from "@/lib/money";

interface InspectionRow {
  id: string;
  inspectionNumber: string;
  productSku: string;
  productName: string;
  source: "Manufacturing" | "Receiving" | "Return";
  sourceRef: string;
  inspectionDate: string | null;
  result: "Pass" | "Fail" | "Warnings" | "InProgress";
  disposition: "Accept" | "Rework" | "Scrap" | "Hold" | null;
  inspector: string;
}

export function QualityInspectionsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<InspectionRow>(
    ["quality", "inspections"],
    "/quality/inspections",
  );

  return (
    <div>
      <PageHeader
        title="Quality Inspections"
        subtitle="Inspection plans, results, disposition, and nonconformance tracking."
        actions={
          <PendingButton variant="primary" reason="notScheduled" slice="B1">
            {t("documentActions.newInspection")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Inspections (${data.total})`}>
          <DataTable<InspectionRow>
            rows={data.items}
            columns={[
              {
                key: "inspectionNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.inspectionNumber}</span>
                ),
              },
              {
                key: "productSku",
                label: "Product",
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "source", label: "Source" },
              {
                key: "sourceRef",
                label: "Source ref",
                render: (r) => (
                  <span className="atlas-mono">{r.sourceRef}</span>
                ),
              },
              {
                key: "inspectionDate",
                label: "Date",
                render: (r) =>
                  r.inspectionDate ? formatDate(r.inspectionDate) : "—",
              },
              { key: "inspector", label: "Inspector" },
              {
                key: "result",
                label: "Result",
                render: (r) =>
                  r.result === "Pass" ? (
                    <StatusBadge status="Pass" />
                  ) : r.result === "Fail" ? (
                    <Badge tone="danger">{r.result}</Badge>
                  ) : r.result === "Warnings" ? (
                    <Badge tone="warning">{r.result}</Badge>
                  ) : (
                    <Badge tone="primary">{r.result}</Badge>
                  ),
              },
              {
                key: "disposition",
                label: "Disposition",
                render: (r) => r.disposition ?? "—",
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
